/**
 * Persistência local do áudio da ata (IndexedDB) + resolução remota (S3/proxy).
 * Metadados ficam no localStorage; blob local é cache do browser.
 * Em LB auth o playback usa presign S3 ou stream via waybee-api.
 */

import isClassDiaryDemo from "../../demo/isClassDiaryDemo";

const DB_NAME = "ataInteligenteAudio";
const DB_VERSION = 1;
const STORE = "audio";

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB indisponível"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error("Falha ao abrir IndexedDB"));
  });
}

export async function saveAtaAudio(id, file) {
  if (!id || !file) return false;
  const db = await openDb();
  const blob = file instanceof Blob ? file : new Blob([file]);
  const record = {
    id: String(id),
    blob,
    mimeType: file.type || blob.type || "audio/mpeg",
    fileName: file.name || null,
    savedAt: new Date().toISOString()
  };
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(record);
    tx.oncomplete = () => {
      db.close();
      resolve(true);
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error("Falha ao salvar áudio"));
    };
  });
}

export async function getAtaAudio(id) {
  if (!id) return null;
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(String(id));
    req.onsuccess = () => {
      db.close();
      resolve(req.result || null);
    };
    req.onerror = () => {
      db.close();
      reject(req.error || new Error("Falha ao ler áudio"));
    };
  });
}

export async function hasAtaAudio(id) {
  try {
    const record = await getAtaAudio(id);
    return !!(record && record.blob);
  } catch (e) {
    return false;
  }
}

export async function createAtaAudioObjectUrl(id) {
  const record = await getAtaAudio(id);
  if (!record || !record.blob) return null;
  const type = record.mimeType || record.blob.type || "audio/mpeg";
  const blob =
    record.blob.type === type
      ? record.blob
      : new Blob([record.blob], { type });
  return {
    url: URL.createObjectURL(blob),
    fileName: record.fileName,
    mimeType: type,
    revoke: true,
    source: "indexeddb"
  };
}

/**
 * Resolve URL de playback: IndexedDB → S3 presign → stream proxy.
 * @param {{ id?: string, meetingId?: string, audioFileName?: string }} ata
 */
export async function resolveAtaAudioPlayback(ata) {
  const draftId = ata && ata.id;
  if (draftId) {
    try {
      const local = await createAtaAudioObjectUrl(draftId);
      if (local && local.url) return local;
    } catch (e) {
      /* segue remoto */
    }
  }

  if (isClassDiaryDemo()) return null;

  const remoteId = (ata && (ata.meetingId || ata.id)) || null;
  if (!remoteId) return null;

  const { default: SmartMinutesService } = await import(
    "../../services/SmartMinutesService"
  );
  let meta = null;
  try {
    meta = await SmartMinutesService.getAudioPlayback(remoteId);
  } catch (e) {
    const status = e && e.response && e.response.status;
    // Sem áudio no servidor (404) ou gateway morto: não estoura modal global.
    if (status === 404 || status === 502 || status === 503) return null;
    throw e;
  }
  if (meta && meta.url) {
    return {
      url: meta.url,
      fileName: (ata && ata.audioFileName) || null,
      mimeType: meta.mime || "audio/mpeg",
      revoke: false,
      source: meta.source || "s3"
    };
  }

  try {
    const blob = await SmartMinutesService.fetchAudioBlob(remoteId);
    if (!blob) return null;
    return {
      url: URL.createObjectURL(blob),
      fileName: (ata && ata.audioFileName) || null,
      mimeType: blob.type || (meta && meta.mime) || "audio/mpeg",
      revoke: true,
      source: "proxy"
    };
  } catch (e) {
    const status = e && e.response && e.response.status;
    if (status === 404 || status === 502 || status === 503) return null;
    throw e;
  }
}

export async function downloadAtaAudioBlob(ata) {
  const record = await getAtaAudio(ata && ata.id);
  if (record && record.blob) {
    const fileName =
      record.fileName ||
      (ata && ata.audioFileName) ||
      `ata-${ata.id}.audio`;
    const url = URL.createObjectURL(record.blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
    return true;
  }

  if (isClassDiaryDemo()) return false;

  try {
    const resolved = await resolveAtaAudioPlayback(ata);
    if (!resolved || !resolved.url) return false;
    const fileName =
      (ata && ata.audioFileName) ||
      `ata-${(ata && ata.id) || "audio"}.mp3`;
    const a = document.createElement("a");
    a.href = resolved.url;
    a.download = fileName;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.click();
    if (resolved.revoke) {
      setTimeout(() => URL.revokeObjectURL(resolved.url), 60_000);
    }
    return true;
  } catch (e) {
    return false;
  }
}

export async function deleteAtaAudio(id) {
  if (!id) return false;
  try {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(String(id));
      tx.oncomplete = () => {
        db.close();
        resolve(true);
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error || new Error("Falha ao remover áudio"));
      };
    });
  } catch (e) {
    return false;
  }
}
